import { useEffect, useState } from "react";
import { ShopifyProduct, fetchShopifyProducts } from "@/lib/shopify";
import { ShopifyProductCard } from "@/components/ShopifyProductCard";
import { Skeleton } from "@/components/ui/skeleton";

interface FeaturedProductsProps {
  limit?: number;
}

const FeaturedProducts = ({ limit = 8 }: FeaturedProductsProps) => {
  const [products, setProducts] = useState<ShopifyProduct[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        setLoading(true);
        const data = await fetchShopifyProducts(250);
        const gradientHandles = ['pro-tour-pink-gradient-overgrips', 'pro-tour-gradient-grips-green', 'pro-tour-yellow-gradient-overgrips', 'pro-tour-gradient-grips-orange-to-white'];
        const xtremeWhiteHandle = 'xtreme-tack-white';
        const brightWhiteHandle = 'bright-white-overgrip';
        const lightPurpleHandle = 'light-purple-pickleball-over-grips';
        const lightBlueHandle = 'light-blue-overgrips';
        const blueDryHandle = 'pro-tour-blue-dry-grips';
        const brightWhiteProduct = data.find((p) => p.node.handle?.toLowerCase() === brightWhiteHandle);
        const blueDryProduct = data.find((p) => p.node.handle?.toLowerCase() === blueDryHandle);
        const gradientProducts = data.filter((p) => {
          const handle = p.node.handle?.toLowerCase() || '';
          return gradientHandles.some((h) => handle === h);
        });
        gradientProducts.sort((a, b) => {
          const aHandle = a.node.handle?.toLowerCase() || '';
          const bHandle = b.node.handle?.toLowerCase() || '';
          return gradientHandles.indexOf(aHandle) - gradientHandles.indexOf(bHandle);
        });
        const xtremeWhiteProduct = data.find((p) => p.node.handle?.toLowerCase() === xtremeWhiteHandle);
        const excludeHandles = [brightWhiteHandle, lightPurpleHandle, lightBlueHandle, ...gradientHandles, xtremeWhiteHandle];
        const otherProducts = data.filter((p) => {
          const handle = p.node.handle?.toLowerCase() || '';
          if (excludeHandles.includes(handle)) return false;
          return true;
        });
        const finalProducts: ShopifyProduct[] = [];
        if (brightWhiteProduct) finalProducts.push(brightWhiteProduct);
        finalProducts.push(...gradientProducts.slice(0, 4));
        if (blueDryProduct) finalProducts.push(blueDryProduct);
        finalProducts.push(...otherProducts.slice(0, 3));
        if (xtremeWhiteProduct) finalProducts.push(xtremeWhiteProduct);
        const remaining = limit - finalProducts.length;
        if (remaining > 0) {
          finalProducts.push(...otherProducts.slice(3, 3 + remaining));
        }
        setProducts(finalProducts.slice(0, limit));
      } catch (err) {
        console.error("Failed to fetch products:", err);
      } finally {
        setLoading(false);
      }
    };
    loadProducts();
  }, [limit]);

  if (loading) {
    return (
      <section className="md:py-32 px-2 sm:px-4 relative py-[30px]">
        <div className="container mx-auto">
          <div className="text-center mb-8 md:mb-12">
            <Skeleton className="h-12 md:h-20 w-3/4 mx-auto mb-2" />
            <Skeleton className="h-5 md:h-8 w-1/2 mx-auto" />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-5 lg:gap-6 max-w-[1400px] mx-auto">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="space-y-3">
                <Skeleton className="aspect-square w-full rounded-lg" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (products.length === 0) return null;

  return (
    <section className="md:py-32 px-2 sm:px-4 relative py-[30px]">
      <div className="container mx-auto">
        <div className="text-center mb-8 md:mb-12">
          <h2 className="font-display mb-2 text-center text-foreground font-bold text-4xl sm:text-5xl md:text-7xl lg:text-9xl animate-fade-in">
            MOST <span className="text-secondary">POPULAR</span>
          </h2>
          <p className="text-muted-foreground animate-fade-in text-base md:text-3xl" style={{ animationDelay: '0.1s' }}>
            Our most popular grips, trusted by pros
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-5 lg:gap-6 max-w-[1400px] mx-auto">
          {products.map((product, index) => {
            const isTopRated = product.node.handle?.toLowerCase() === 'bright-white-overgrip';
            return (
              <div
                key={product.node.id}
                className="animate-fade-in-up transition-transform duration-300 hover:-translate-y-2 hover:scale-[1.02]"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <ShopifyProductCard product={product} showTopRatedBadge={isTopRated} />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FeaturedProducts;
